/**
 * This renders all the customer app routes and handles the initial
 *
 * Tried to use AuthenticatedRoute.tsx for the routes,
 *   but there are edge cases in the customer app that doesn't work with this approach.
 * Because of this, we had to render all Routes in the same Switch
 *   and render the Redirects dynamically, so that we don't get Redirected randomly
 */

import { useEffect, useState } from 'react';

import { useUnit } from 'effector-react';
import { parse } from 'query-string';
import { matchPath, Redirect, Switch, useHistory, useLocation } from 'react-router-dom';

import { isProd } from '@m/config/FeatureFlags';
import { useCustomerFeatureFlag } from '@m/hooks/data/featureFlags';
import { vehicleStore } from '@m/state/vehicle';
import { onboardingSessionStore } from 'apps/customer/containers/Onboarding/container/onboardingSession';
import SignInOnboardingGate from 'apps/customer/containers/Onboarding/container/SignInOnboardingGate';
import { hasEnterpriseInvitationParams } from 'apps/customer/containers/Onboarding/onboardingExperimentCohort';
import membershipStore from 'apps/customer/state/membership/store';
import { paymentStore } from 'apps/customer/state/payment';
import { routeStore, setSignInLandingPage } from 'apps/customer/state/route';
import { todoStore } from 'apps/customer/state/todo';
import { userStore } from 'apps/customer/state/user';
import { visitStore } from 'apps/customer/state/visit';
import {
  isSignInOnboardingPath,
  isTaskOnboardingPath,
} from 'apps/customer/utils/signInOnboardingPathGate';

import {
  authPaths,
  DefaultOnboardingRoutes,
  isPublicPath,
  ProtectedRoutes,
  PublicRoutes,
  StartParkingOnboardingRoutes,
  TaskItemRoutes,
} from './paths';
import {
  customerRoutesList,
  getUnauthenticatedFallback,
  onboardingRoutesList,
  publicRoutesList,
  taskRoutesList,
} from './routes';

import type { SignInOnboardingExperimentState } from 'apps/customer/containers/Onboarding/container/useSignInOnboardingExperiment';
import type { SignInOnboardingExperimentCohort } from 'apps/customer/containers/Onboarding/onboardingExperimentCohort';

function matchesPublicPath(pathname: string): boolean {
  return isPublicPath(pathname);
}

function matchesAuthPath(pathname: string): boolean {
  return authPaths.some((path) => !!matchPath(pathname, { path, exact: true }));
}

function isEnterpriseInvitationEntryPath(pathname: string): boolean {
  return pathname === '/login' || pathname === '/welcome';
}

function isRequiredTaskPath(pathname: string): boolean {
  return (
    isTaskOnboardingPath(pathname) ||
    pathname === TaskItemRoutes.Tasks ||
    pathname.startsWith(`${TaskItemRoutes.Tasks}/`) ||
    pathname === ProtectedRoutes.ConfirmMissedEntryPage
  );
}

type Props = {
  isStpOnboarding: boolean;
  onboardingExperimentCohort: SignInOnboardingExperimentCohort;
  signInOnboardingExperiment: SignInOnboardingExperimentState;
  stpOnboardingExperiment: SignInOnboardingExperimentState;
  userDataInitialized: boolean;
};

export default function CustomerRoutes({
  isStpOnboarding,
  onboardingExperimentCohort,
  signInOnboardingExperiment,
  stpOnboardingExperiment,
  userDataInitialized,
}: Props) {
  const location = useLocation();
  const history = useHistory();

  // tracks if the initial page load routing has occurred yet
  const [hasPageLoaded, setHasPageLoaded] = useState(false);

  const {
    user,
    authenticated,
    enterpriseUser,
    isReferredMember,
    status: {
      verifyCodeSuccess,
      signUpRegisterSuccess,
      parseInvitationCodeSuccess: isInviteWelcome,
    },
  } = useUnit(userStore);
  const {
    hasRequiredTasks,
    hasFetchedTodo,
    onboardingTasks,
    hasPaymentTask,
    hasPendingTasks,
    expressOnboardingExperimentGroup,
  } = useUnit(todoStore);
  const { signInLandingPage, promptUserSignIn } = useUnit(routeStore);
  const { individualSubscriptionInvitationId } = useUnit(membershipStore);
  const { isFlowComplete, pendingSubscriptionResume } = useUnit(onboardingSessionStore);
  const { defaultPayment } = useUnit(paymentStore);
  const { vehicles } = useUnit(vehicleStore);
  const { hasActiveVisit, hasInReviewVisit } = useUnit(visitStore);

  const { data: discoveryMapV2 = false, isLoading: isDiscoveryMapV2Loading } =
    useCustomerFeatureFlag('DiscoveryMapV2');
  const isSignInOnboardingRoute = isSignInOnboardingPath(location.pathname);
  const blocksLegacySignInRouting =
    signInOnboardingExperiment.status === 'pending' ||
    signInOnboardingExperiment.status === 'treatment';
  const isSignInOnboardingLoading =
    isSignInOnboardingRoute &&
    (signInOnboardingExperiment.status === 'pending' ||
      (signInOnboardingExperiment.status === 'treatment' &&
        authenticated === true &&
        !userDataInitialized));
  const shouldResumeSubscriptionOnboarding =
    !!pendingSubscriptionResume &&
    onboardingExperimentCohort !== 'agnostic' &&
    signInOnboardingExperiment.status === 'treatment' &&
    !isFlowComplete &&
    hasFetchedTodo &&
    hasRequiredTasks === false &&
    hasPaymentTask === false &&
    !isSignInOnboardingRoute &&
    !isRequiredTaskPath(location.pathname);
  const shouldStartIndividualSubscriptionOnboarding =
    !isFlowComplete &&
    onboardingExperimentCohort === 'individual' &&
    signInOnboardingExperiment.status === 'treatment' &&
    authenticated === true &&
    !!individualSubscriptionInvitationId &&
    location.pathname === ProtectedRoutes.MembershipPage;

  const { data: physicalCardEnabled = false, isLoading: isPhysicalCardFlagLoading } =
    useCustomerFeatureFlag('consumer_physical-card-fallback_v1');

  /**
   * on page load navigator
   * waits for all the required data to be loaded before making the decision
   *
   * there are 3 states the user can be in when the first land on the page.
   * depending on the state, we need to handle routing differently.
   * to evaluate the state, we need to fetch user data, and todos
   *
   * if public path, go to public path
   * if enterprise invite flow, diff flow than this one
   *
   * Step Checklist
   * registered users - user has verified phone number and accepted ToS
   *   1. if user has confirmVisit task, display express STP landing page - /select-visit-duration
   *   2. if user has express variation:
   *     a. if user has paymentTask, go to /tasks (will deprecate once we handle payment overdue)
   *     b. if user has any required tasks (not paymentTask):
   *       i. if only task is missedEntry, go to /tasks (missed entry page is WIP, so use legacy)
   *       ii. else go to - /onboard
   *     c. if user has non-required tasks (addPaymentMethod, etc), go to home or visit page - /
   *     d. else go to /
   *
   * unregistered user (QR code scan or only verified phone number. user session found)
   *   1. if user has confirmVisit task, display express STP landing page - /select-visit-duration
   *   2. if user has express variation:
   *     a. if user has any required tasks (not paymentTask), go to express vision - /onboard
   *     b. if user has non-required tasks, go to /onboard (skippable addPaymentMethod only?)
   *     c. if pathname is valid unauth route, like /welcome (invite link), go to pathname
   *     d. else /sign-in
   *
   * no user session (visits app.metropolis.io or mpolis.io/welcome)
   *   1. go to /sign-in
   */
  useEffect(() => {
    // todo: what's the cleanest way of bypassing this crazy flow
    // should we forget about forcing users onto pages based on user state? maybe forcing on some pages only?
    // ex. if someone is in express flow and they visit app.metropolis.io/
    //     can we not redirect them to /onboard and just put them onto dashboard?
    //     they're most likely already on /onboard or will probably rescan anyways right?
    if (isSignInOnboardingRoute && signInOnboardingExperiment.status !== 'control') {
      if (
        signInOnboardingExperiment.status === 'treatment' &&
        (authenticated !== true || userDataInitialized)
      ) {
        setHasPageLoaded(true);
      }
      return;
    }

    if (shouldStartIndividualSubscriptionOnboarding) {
      history.replace(
        `${DefaultOnboardingRoutes.SignIn}?subInviteId=${encodeURIComponent(
          individualSubscriptionInvitationId,
        )}`,
      );
      return;
    }

    if (shouldResumeSubscriptionOnboarding && pendingSubscriptionResume) {
      history.replace(`${DefaultOnboardingRoutes.SignIn}${pendingSubscriptionResume.search}`);
      return;
    }

    if (enterpriseUser) {
      if (
        !isFlowComplete &&
        signInOnboardingExperiment.status === 'treatment' &&
        isEnterpriseInvitationEntryPath(location.pathname) &&
        hasEnterpriseInvitationParams(location.search)
      ) {
        history.replace(`${DefaultOnboardingRoutes.SignIn}${location.search}`);
      } else if (
        signInOnboardingExperiment.status === 'treatment' &&
        isRequiredTaskPath(location.pathname)
      ) {
        setHasPageLoaded(true);
      } else if (signInOnboardingExperiment.status === 'treatment') {
        setHasPageLoaded(true);
      }
      return;
    }

    if (authenticated === false && !!parse(location.search).validation) {
      if (location.pathname !== DefaultOnboardingRoutes.SignIn) {
        history.push(DefaultOnboardingRoutes.SignIn);
      }
      return;
    }

    // The Home hub and map are viewable directly, so normally we leave visitors on them. But a
    // user with a session who still owes onboarding must not be parked here — fall through to the
    // redirects below so they still reach /onboard etc. This covers both the authenticated branch
    // (required/payment tasks) and the session-but-not-yet-authenticated branch (e.g. a vision
    // validation QR signup, which also redirects on any pending task). We also fall through while
    // todos are still loading, so setHasPageLoaded(true) can't lock in the landing before we know.
    const hasUserSession = authenticated === true || Object.keys(user).length > 0;
    const owesOnboarding =
      hasUserSession &&
      (!hasFetchedTodo ||
        hasRequiredTasks ||
        hasPaymentTask ||
        (authenticated !== true && hasPendingTasks));
    const canRemainOnDiscoveryPage =
      (location.pathname === ProtectedRoutes.HomePage ||
        location.pathname === ProtectedRoutes.ParkNowPage) &&
      !owesOnboarding;

    // revert to old flow
    // Hold the fast-path for /code/:qrToken until the flag resolves so the route
    // is present in the Switch before we mark the page loaded — otherwise the
    // unauthenticated fallback fires while physicalCardEnabled is still pending.
    const isOnPhysicalCardPath = !!matchPath(location.pathname, {
      path: PublicRoutes.PhysicalCard,
      exact: true,
    });
    if (
      (matchesPublicPath(location.pathname) || canRemainOnDiscoveryPage) &&
      location.pathname !== PublicRoutes.KioskValidation &&
      !(isOnPhysicalCardPath && isPhysicalCardFlagLoading)
    ) {
      setHasPageLoaded(true);
      return;
    }

    // get if user is set
    if (
      !hasPageLoaded &&
      authenticated !== null &&
      !isDiscoveryMapV2Loading &&
      !(isOnPhysicalCardPath && isPhysicalCardFlagLoading)
    ) {
      if (authenticated) {
        // registered users
        if (hasFetchedTodo) {
          if (hasPaymentTask) {
            // 2a
            if (!location.pathname.startsWith(TaskItemRoutes.Tasks)) {
              history.push(TaskItemRoutes.Tasks);
            }
          } else if (hasRequiredTasks) {
            // 2b
            if (
              expressOnboardingExperimentGroup !== 'treatment' &&
              onboardingTasks?.missedEntryStartVisit &&
              onboardingTasks.list.length === 1
            ) {
              // 2bi — legacy confirm entry page (control group only)
              if (!location.pathname.startsWith(ProtectedRoutes.ConfirmMissedEntryPage)) {
                history.push(ProtectedRoutes.ConfirmMissedEntryPage);
              }
            } else {
              // 2bii
              history.push(
                history.location.search && !isProd
                  ? StartParkingOnboardingRoutes.Onboard + history.location.search
                  : StartParkingOnboardingRoutes.Onboard,
              );
            }
          } else if (!matchesAuthPath(location.pathname)) {
            // 2c, 2d
            // if visit history page, vehicle settings (wildcard matching)
            if (
              !location.pathname.match(/^\/receipt\/[0-9A-Za-z-]+$/) &&
              !location.pathname.match(/^\/visit\/[0-9A-Za-z]+$/) &&
              !location.pathname.match(/^\/vehicle\/[0-9A-Za-z]+$/) &&
              !location.pathname.match(/^\/vehicle\/[0-9A-Za-z]+\/drivers$/) &&
              !location.pathname.match(/^\/pass\/[0-9A-Za-z-]+$/)
            ) {
              history.push(signInLandingPage || ProtectedRoutes.ForwardSlash);
            }
          }

          setHasPageLoaded(true);
        }
      } else if (Object.keys(user).length > 0) {
        if (hasFetchedTodo) {
          if ((hasRequiredTasks && !hasPaymentTask) || (!hasRequiredTasks && hasPendingTasks)) {
            // 2a - only show the required payment task after they've registered (overdue payment)
            // 2b - if there are pending tasks that are not required, then have them fill it out
            history.push(
              history.location.search && !isProd
                ? StartParkingOnboardingRoutes.Onboard + history.location.search
                : StartParkingOnboardingRoutes.Onboard,
            );
          } else if (promptUserSignIn || signInLandingPage) {
            history.push(signInLandingPage || DefaultOnboardingRoutes.SignIn);
          } else if (onboardingRoutesList.indexOf(location.pathname) === -1) {
            // 2c, 2d
            history.push(DefaultOnboardingRoutes.SignIn);
          }
          setHasPageLoaded(true);
        }
      } else if (location.pathname.match(/^\/claim\/[^/]+$/)) {
        // user trying to claim via link / QR - redirect to sign-in
        history.push(DefaultOnboardingRoutes.SignIn);
        setHasPageLoaded(true);
      } else {
        // no user session
        // Note: history.push triggers a location change before setHasPageLoaded
        // is applied, so this block can re-fire. Guard each branch accordingly.
        const cameFromKiosk = location.pathname === PublicRoutes.KioskValidation;
        const alreadyOnOnboardingPath = onboardingRoutesList.indexOf(location.pathname) > -1;
        if (discoveryMapV2) {
          // Send to the home/landing page only from the app root or a failed kiosk bootstrap
          // (no session). All other unauthenticated paths (e.g. /join, /sign-in, deep links) go
          // to sign-in so we don't override their intent or a queued signInLandingPage.
          // Also exclude validation links — useUserInitialization handles those and routes
          // to /sign-in after applying the validation, but its setSignInLandingPage call loses
          // the race with this effect's stale closure over signInLandingPage.
          const hasValidationParam = !!parse(location.search).validation;
          const unauthenticatedDiscoveryMapRedirect =
            (location.pathname === ProtectedRoutes.ForwardSlash || cameFromKiosk) &&
            !signInLandingPage &&
            !hasValidationParam
              ? ProtectedRoutes.HomePage
              : DefaultOnboardingRoutes.SignIn;
          if (
            (cameFromKiosk || !alreadyOnOnboardingPath) &&
            location.pathname !== unauthenticatedDiscoveryMapRedirect
          ) {
            history.push(unauthenticatedDiscoveryMapRedirect);
          }
        } else if (
          (cameFromKiosk || !alreadyOnOnboardingPath) &&
          location.pathname !== DefaultOnboardingRoutes.SignIn
        ) {
          history.push(DefaultOnboardingRoutes.SignIn);
        }
        setHasPageLoaded(true);
      }
    }
  }, [
    hasPaymentTask,
    hasPendingTasks,
    user,
    authenticated,
    hasFetchedTodo,
    hasRequiredTasks,
    onboardingTasks,
    expressOnboardingExperimentGroup,
    enterpriseUser,
    hasPageLoaded,
    location,
    history,
    discoveryMapV2,
    isDiscoveryMapV2Loading,
    isPhysicalCardFlagLoading,
    promptUserSignIn,
    signInLandingPage,
    isSignInOnboardingRoute,
    signInOnboardingExperiment.status,
    shouldResumeSubscriptionOnboarding,
    pendingSubscriptionResume,
    isFlowComplete,
    individualSubscriptionInvitationId,
    shouldStartIndividualSubscriptionOnboarding,
    userDataInitialized,
  ]);

  /**
   * enterprise user flow
   * if missing email or not registered, go to sign up
   * if missing vehicle, go vehicle
   * else, go membership
   *
   * will there be a problem if user has todos?
   */
  useEffect(() => {
    if (hasPageLoaded || blocksLegacySignInRouting) {
      return;
    }
    if (enterpriseUser) {
      const { isRegistered, email } = user;
      if (email && isRegistered) {
        if (vehicles?.length === 0) {
          history.push(DefaultOnboardingRoutes.Vehicle + history.location.search);
          setHasPageLoaded(true);
        } else if (vehicles && vehicles.length > 0) {
          history.push(`/membership${history.location.search}`);
          setHasPageLoaded(true);
        }
      } else {
        history.push(`${DefaultOnboardingRoutes.SignIn}${history.location.search}`);
        setHasPageLoaded(true);
      }
    }
  }, [enterpriseUser, user, vehicles, hasPageLoaded, history, blocksLegacySignInRouting]);

  /**
   * NOTE: is this still used other than for visit receipts?
   *
   * Override landing page. Some links or QR codes will route the user somewhere else
   **/
  useEffect(() => {
    if (blocksLegacySignInRouting) {
      return;
    }
    // If signing in, then SignUpContainer will handle the sign in logic
    if (signUpRegisterSuccess === null && verifyCodeSuccess === null) {
      if (
        userDataInitialized &&
        authenticated &&
        hasRequiredTasks === false && // required tasks take priority
        signInLandingPage
      ) {
        if (promptUserSignIn) {
          if (!user.email) {
            history.push(DefaultOnboardingRoutes.SignUp);
            return;
          }
          if (!vehicles?.length) {
            // need to collect more information
            // again, ideally this is a /task route
            history.push(DefaultOnboardingRoutes.Vehicle);
            return;
          }
          if (defaultPayment === null) {
            history.push(DefaultOnboardingRoutes.PaymentMethod);
            return;
          }
        }
        // Only navigate if not already on that page. Use history.location
        // (always current — not a stale closure).
        // If signInLandingPage has a query string (e.g. /promo?code=X), we must
        // land on the exact path+query. If it has no query, pathname equality is
        // enough — the user may already have query params (e.g. /park-now?lat=...)
        // that should not be overwritten.
        const qIdx = signInLandingPage.indexOf('?');
        const landingPathname = qIdx === -1 ? signInLandingPage : signInLandingPage.slice(0, qIdx);
        const landingSearch = qIdx === -1 ? '' : signInLandingPage.slice(qIdx);
        const alreadyThere = landingSearch
          ? history.location.pathname === landingPathname &&
            history.location.search === landingSearch
          : history.location.pathname === landingPathname;
        if (!alreadyThere) {
          history.push(signInLandingPage);
        }
        setSignInLandingPage(null);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    history,
    userDataInitialized,
    promptUserSignIn,
    verifyCodeSuccess,
    signUpRegisterSuccess,
    blocksLegacySignInRouting,
    // Purposely leaving out vehicles, hasRequiredTasks, and defaultPayment
    // so this hook isn't run if any of these are updated
  ]);

  return (
    <Switch>
      {publicRoutesList(discoveryMapV2, physicalCardEnabled).map(
        ({ component: Component, ...props }) =>
          Component && <Component key={props.path} {...props} />,
      )}

      {authenticated &&
        hasFetchedTodo &&
        hasRequiredTasks &&
        taskRoutesList().map(
          ({ component: Component, ...props }) =>
            Component && (
              <Component
                key={typeof props.path === 'string' ? props.path : props.path[0]}
                {...props}
              />
            ),
        )}

      {hasPageLoaded &&
        authenticated !== null &&
        customerRoutesList({
          isInviteWelcome,
          isReferredMember,
          hasActiveVisit,
          hasInReviewVisit,
          discoveryMapV2,
          isStpOnboarding,
          stpOnboardingExperiment,
        }).map((route) => {
          if (route === null) {
            return null;
          }
          const { component: Component, ...props } = route;
          return (
            Component && (
              <Component
                key={typeof props.path === 'string' ? props.path : props.path[0]}
                {...props}
              />
            )
          );
        })}

      {(hasPageLoaded || isSignInOnboardingLoading) && authenticated !== null && (
        <SignInOnboardingGate
          experiment={
            isSignInOnboardingLoading ? { status: 'pending' } : signInOnboardingExperiment
          }
        />
      )}

      {hasPageLoaded &&
        authenticated === false &&
        !isDiscoveryMapV2Loading &&
        getUnauthenticatedFallback(discoveryMapV2)}
      {hasPageLoaded &&
        authenticated &&
        hasRequiredTasks === false &&
        !matchesPublicPath(location.pathname) && <Redirect to={ProtectedRoutes.ForwardSlash} />}
      {hasPageLoaded && authenticated && hasRequiredTasks && <Redirect to={TaskItemRoutes.Tasks} />}
    </Switch>
  );
}
